import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Book, ExternalLink, Search, Download, Sun, Moon, Filter } from 'lucide-react';
import Button from '@/components/Button';
import { supabase } from '@/integrations/supabase/client';
import { Input } from '@/components/ui/input';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '@/components/ui/pagination';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Tables } from '@/integrations/supabase/types';

type Resource = Tables<"resources">;
type FilterKey = 'category' | 'format' | 'region' | 'year';

const ResourcesPage = () => {
  const [resources, setResources] = useState<Resource[]>([]);
  const [filteredResources, setFilteredResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [filters, setFilters] = useState<Record<FilterKey, string | null>>({
    category: null,
    format: null,
    region: null,
    year: null
  });
  const [filterOptions, setFilterOptions] = useState<Record<FilterKey, string[]>>({
    category: [],
    format: [],
    region: [],
    year: []
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const itemsPerPage = 12;

  // Fetch resources
  useEffect(() => {
    const fetchResources = async () => {
      try {
        setLoading(true);
        const {
          data,
          error
        } = await supabase.from('resources').select('*').order('created_at', {
          ascending: false
        });
        if (error) throw new Error(error.message);
        setResources(data || []);
        setLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error occurred');
        setLoading(false);
      }
    };
    fetchResources();
  }, []);

  // Extract filter options
  useEffect(() => {
    if (!resources.length) return;
    const options: Record<FilterKey, Set<string>> = {
      category: new Set(),
      format: new Set(),
      region: new Set(),
      year: new Set()
    };
    resources.forEach(resource => {
      if (resource.category) options.category.add(resource.category);
      if (resource.format) options.format.add(resource.format);
      if (resource.region) options.region.add(resource.region);
      if (resource.year) options.year.add(resource.year.toString());
    });
    setFilterOptions({
      category: Array.from(options.category).sort(),
      format: Array.from(options.format).sort(),
      region: Array.from(options.region).sort(),
      year: Array.from(options.year).sort((a, b) => parseInt(b) - parseInt(a))
    });
  }, [resources]);

  // Filter and search resources
  useEffect(() => {
    if (!resources.length) return;
    let result = [...resources];

    // Apply filters
    if (filters.category) {
      result = result.filter(r => r.category === filters.category);
    }
    if (filters.format) {
      result = result.filter(r => r.format === filters.format);
    }
    if (filters.region) {
      result = result.filter(r => r.region === filters.region);
    }
    if (filters.year) {
      result = result.filter(r => r.year?.toString() === filters.year);
    }

    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(r => r.title.toLowerCase().includes(query) || r.description?.toLowerCase().includes(query) || r.tags?.some(tag => tag.toLowerCase().includes(query)));
    }
    setFilteredResources(result);
    // Reset to first page when filters/search changes
    setCurrentPage(1);
  }, [resources, filters, searchQuery]);

  // Handle filter changes
  const handleFilterChange = (key: FilterKey, value: string | null) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Handle theme toggle
  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  // Calculate pagination
  const totalPages = Math.ceil(filteredResources.length / itemsPerPage);
  const currentItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredResources.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredResources, currentPage, itemsPerPage]);

  // Generate pagination items
  const paginationItems = useMemo(() => {
    const items = [];

    // For small number of pages, show all
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        items.push(i);
      }
      return items;
    }

    // For more pages, show first, last, and around current
    items.push(1);
    if (currentPage > 3) {
      items.push('ellipsis1');
    }

    // Pages around current
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let i = start; i <= end; i++) {
      items.push(i);
    }
    if (currentPage < totalPages - 2) {
      items.push('ellipsis2');
    }
    if (totalPages > 1) {
      items.push(totalPages);
    }
    return items;
  }, [totalPages, currentPage]);
  return <div className="relative min-h-screen">
      {/* Background Image - Updated to match homepage style */}
      <div 
        className="fixed inset-0 z-0 bg-black/30" 
        style={{
          backgroundImage: "url('/lovable-uploads/f07aa100-de6e-47ad-b383-bca26a39b25b.png')",
          backgroundSize: "100% auto",
          backgroundPosition: "top center",
          backgroundRepeat: "no-repeat",
          backgroundAttachment: "scroll"
        }} 
      />
      
      <div className="relative z-10">
        {/* Hero Section with transparent dark background */}
        <div className={`py-16 ${isDarkMode ? 'text-white' : 'text-white'}`}>
          <div className="container-custom">
            <div className="flex flex-col md:flex-row md:items-center justify-between">
              <div>
                <h1 className="text-4xl md:text-5xl font-bold drop-shadow-lg">E-Library</h1>
                <p className="text-xl mt-4 drop-shadow-md">
                  Digital resources for the Yoruba community
                </p>
              </div>
              
              <button 
                onClick={() => setIsDarkMode(!isDarkMode)} 
                className={`mt-4 md:mt-0 p-2 rounded-full ${isDarkMode ? 'bg-gray-700/70 hover:bg-gray-600/70 backdrop-blur-sm' : 'bg-black/50 hover:bg-black/70 backdrop-blur-sm'}`} 
                aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDarkMode ? <Sun size={20} className="text-white" /> : <Moon size={20} className="text-white" />}
              </button>
            </div>
          </div>
        </div>

        <div className="container-custom pb-16 bg-orange-400">
          {/* Search and filter section - Made darker and more transparent */}
          <div className={`${isDarkMode ? 'bg-gray-900/70 backdrop-blur-sm border border-gray-700/50' : 'bg-black/50 backdrop-blur-sm border border-white/20'} 
            rounded-lg p-4 md:p-6 mb-8`}>
            <div className="flex flex-col md:flex-row gap-4 items-center">
              <div className="relative w-full md:flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-300" size={18} />
                <Input type="text" placeholder="Search by title, description or tags..." className={`pl-10 w-full ${isDarkMode ? 'bg-gray-800/70 border-gray-700 text-white placeholder:text-gray-400' : 'bg-black/40 border-gray-700 text-white placeholder:text-gray-300'}`} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
              </div>
              
              <div className="flex items-center gap-2 w-full md:w-auto">
                <Button variant={isFilterOpen ? "outline" : "primary"} onClick={() => setIsFilterOpen(!isFilterOpen)} className="md:hidden w-full">
                  <Filter size={16} />
                  Filters
                </Button>
                
                <div className="hidden md:flex gap-2">
                  <FilterDropdown label="Category" options={filterOptions.category} value={filters.category} onChange={value => handleFilterChange('category', value)} isDarkMode={isDarkMode} />
                  <FilterDropdown label="Format" options={filterOptions.format} value={filters.format} onChange={value => handleFilterChange('format', value)} isDarkMode={isDarkMode} />
                  <FilterDropdown label="Region" options={filterOptions.region} value={filters.region} onChange={value => handleFilterChange('region', value)} isDarkMode={isDarkMode} />
                  <FilterDropdown label="Year" options={filterOptions.year} value={filters.year} onChange={value => handleFilterChange('year', value)} isDarkMode={isDarkMode} />
                </div>
              </div>
            </div>
            
            {/* Collapsible filters for mobile - Kept with dark styles */}
            <Collapsible open={isFilterOpen} className="md:hidden mt-4">
              <CollapsibleContent className="space-y-2">
                <FilterDropdown label="Category" options={filterOptions.category} value={filters.category} onChange={value => handleFilterChange('category', value)} isDarkMode={isDarkMode} fullWidth />
                <FilterDropdown label="Format" options={filterOptions.format} value={filters.format} onChange={value => handleFilterChange('format', value)} isDarkMode={isDarkMode} fullWidth />
                <FilterDropdown label="Region" options={filterOptions.region} value={filters.region} onChange={value => handleFilterChange('region', value)} isDarkMode={isDarkMode} fullWidth />
                <FilterDropdown label="Year" options={filterOptions.year} value={filters.year} onChange={value => handleFilterChange('year', value)} isDarkMode={isDarkMode} fullWidth />
              </CollapsibleContent>
            </Collapsible>
            
            {/* Active filters display - Updated to be more visible */}
            {Object.values(filters).some(Boolean) && <div className="flex flex-wrap gap-2 mt-3">
                {Object.entries(filters).map(([key, value]) => value ? <div key={key} className={`px-3 py-1 text-sm rounded-full flex items-center gap-1 ${isDarkMode ? 'bg-omoduduwa-terracotta/50 text-white' : 'bg-omoduduwa-terracotta/60 text-white'}`}>
                      <span>{key}: {value}</span>
                      <button onClick={() => handleFilterChange(key as FilterKey, null)} className="ml-1 hover:text-gray-200">
                        ×
                      </button>
                    </div> : null)}
                
                <button className="text-sm underline text-gray-300" onClick={() => setFilters({
              category: null,
              format: null,
              region: null,
              year: null
            })}>
                  Clear all
                </button>
              </div>}
          </div>

          {/* Resource count and loading state - Updated to have dark background */}
          <div className="mb-6 flex justify-between items-center">
            <p className="text-white font-medium backdrop-blur-sm inline-block px-3 py-1 rounded-md bg-black/50">
              {loading ? 'Loading resources...' : `${filteredResources.length} resources found`}
            </p>
          </div>
          
          {/* Resources grid - Updated card styling to be dark and semi-transparent */}
          {loading ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, index) => <ResourceCardSkeleton key={index} isDarkMode={isDarkMode} />)}
            </div> : error ? <div className="text-center py-16 text-white bg-black/70 backdrop-blur-sm rounded-lg p-8">
              <p>Error loading resources: {error}</p>
              <Button className="mt-4" onClick={() => window.location.reload()}>Try Again</Button>
            </div> : filteredResources.length === 0 ? <div className="text-center py-16 text-white bg-black/70 backdrop-blur-sm rounded-lg p-8">
              <p>No resources match your search criteria.</p>
              <Button className="mt-4" onClick={() => {
            setSearchQuery('');
            setFilters({
              category: null,
              format: null,
              region: null,
              year: null
            });
          }}>
                Clear Filters
              </Button>
            </div> : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {currentItems.map(resource => <ResourceCard key={resource.id} resource={resource} isDarkMode={isDarkMode} />)}
            </div>}
          
          {/* Pagination - Made darker against background */}
          {!loading && filteredResources.length > 0 && <Pagination className="mt-12">
              <PaginationContent className={`${isDarkMode ? 'bg-gray-900/70' : 'bg-black/60'} backdrop-blur-sm p-2 rounded-lg`}>
                <PaginationItem>
                  <PaginationPrevious onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} className={`${currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'} text-white`} aria-disabled={currentPage === 1} />
                </PaginationItem>
                
                {paginationItems.map((item, i) => typeof item === 'number' ? <PaginationItem key={i}>
                      <PaginationLink isActive={currentPage === item} onClick={() => setCurrentPage(item)} className="text-white hover:bg-gray-700/50">
                        {item}
                      </PaginationLink>
                    </PaginationItem> : <PaginationItem key={item}>
                      <PaginationEllipsis className="text-white" />
                    </PaginationItem>)}
                
                <PaginationItem>
                  <PaginationNext onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} className={`${currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'} text-white`} aria-disabled={currentPage === totalPages} />
                </PaginationItem>
              </PaginationContent>
            </Pagination>}

          {/* Request resources section - Updated to be dark and semi-transparent */}
          <div className="mt-16 text-center p-8 rounded-lg backdrop-blur-sm border border-omoduduwa-terracotta/30 bg-gray-950">
            <h3 className="text-2xl font-bold mb-4 text-white">
              Need More Resources?
            </h3>
            <p className="mb-6 max-w-2xl mx-auto text-gray-200">
              If you're looking for specific resources not listed here, or would like to contribute materials 
              to our resource library, please contact our team.
            </p>
            <Link to="/contact" className="inline-block">
              <Button variant="primary" size="lg">
                Request Resources
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>;
};

// Resource Card Component - Updated to be dark and semi-transparent
const ResourceCard = ({
  resource,
  isDarkMode
}: {
  resource: Resource;
  isDarkMode: boolean;
}) => {
  return <Card className={`h-full flex flex-col ${isDarkMode ? 'bg-gray-900/70 border-gray-700/50 text-white backdrop-blur-sm' : 'bg-black/60 border-white/20 text-white backdrop-blur-sm'}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <CardTitle className="text-lg truncate text-white">
            {resource.title}
          </CardTitle>
          {resource.format && <span className={`px-2 py-1 text-xs rounded ${isDarkMode ? 'bg-gray-800 text-gray-200' : 'bg-black/70 text-gray-200'}`}>
              {resource.format}
            </span>}
        </div>
        {resource.year && <div className="text-xs text-gray-300">
            {resource.year}
          </div>}
      </CardHeader>
      <CardContent className="pb-3 flex-grow">
        <CardDescription className="line-clamp-2 text-gray-300">
          {resource.description || "No description available"}
        </CardDescription>
        
        {resource.tags && resource.tags.length > 0 && <div className="mt-4 flex flex-wrap gap-1">
            {resource.tags.slice(0, 3).map((tag, index) => <span key={index} className={`px-2 py-0.5 text-xs rounded-full ${isDarkMode ? 'bg-omoduduwa-terracotta/40 text-white' : 'bg-omoduduwa-terracotta/50 text-white'}`}>
                {tag}
              </span>)}
            {resource.tags.length > 3 && <span className={`px-2 py-0.5 text-xs rounded-full ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-black/70 text-gray-200'}`}>
                +{resource.tags.length - 3}
              </span>}
          </div>}
      </CardContent>
      <CardFooter className={`pt-2 ${isDarkMode ? 'border-t border-gray-700' : 'border-t border-gray-700'}`}>
        {resource.link ? <a href={resource.link} target="_blank" rel="noopener noreferrer" className="w-full">
            <Button variant="outline" className={`w-full flex items-center justify-center ${isDarkMode ? 'border-omoduduwa-terracotta/70 text-omoduduwa-terracotta hover:bg-omoduduwa-terracotta/20' : 'border-omoduduwa-terracotta/70 text-omoduduwa-terracotta hover:bg-omoduduwa-terracotta/20'}`}>
              <Download size={16} className="mr-2" />
              Access Resource
            </Button>
          </a> : <Button variant="outline" className="w-full text-gray-400" disabled>
            No Link Available
          </Button>}
      </CardFooter>
    </Card>;
};

// Filter Dropdown Component - Updated with dark styles
interface FilterDropdownProps {
  label: string;
  options: string[];
  value: string | null;
  onChange: (value: string | null) => void;
  isDarkMode: boolean;
  fullWidth?: boolean;
}
const FilterDropdown = ({
  label,
  options,
  value,
  onChange,
  isDarkMode,
  fullWidth = false
}: FilterDropdownProps) => {
  if (!options.length) return null;
  return <DropdownMenu>
      <DropdownMenuTrigger className={`${isDarkMode ? 'bg-gray-800/90 border-gray-700 text-white hover:bg-gray-700' : 'bg-black/60 border-gray-600 text-white hover:bg-black/80'} flex items-center gap-2 px-3 py-2 rounded border ${value ? 'border-omoduduwa-terracotta' : ''} ${fullWidth ? 'w-full' : ''}`}>
        <span>{value ? `${label}: ${value}` : label}</span>
        <Filter size={14} />
      </DropdownMenuTrigger>
      <DropdownMenuContent className={isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-gray-900 border-gray-800 text-white"}>
        <DropdownMenuItem onClick={() => onChange(null)} className={`${!value ? 'font-bold' : ''} ${isDarkMode ? 'text-white hover:bg-gray-700' : 'text-white hover:bg-gray-800'}`}>
          All
        </DropdownMenuItem>
        {options.map(option => <DropdownMenuItem key={option} onClick={() => onChange(option)} className={`${value === option ? 'font-bold' : ''} ${isDarkMode ? 'text-white hover:bg-gray-700' : 'text-white hover:bg-gray-800'}`}>
            {option}
          </DropdownMenuItem>)}
      </DropdownMenuContent>
    </DropdownMenu>;
};

// Resource Card Skeleton for loading state - Updated with dark style
const ResourceCardSkeleton = ({
  isDarkMode
}: {
  isDarkMode: boolean;
}) => {
  return <div className={`p-4 rounded-lg border h-52 ${isDarkMode ? 'bg-gray-900/70 border-gray-700 animate-pulse' : 'bg-black/60 border-gray-700 animate-pulse'}`}>
      <div className={`h-6 mb-2 rounded ${isDarkMode ? 'bg-gray-800' : 'bg-gray-800'}`}></div>
      <div className={`h-4 w-1/4 mb-4 rounded ${isDarkMode ? 'bg-gray-800' : 'bg-gray-800'}`}></div>
      <div className={`h-4 mb-2 rounded ${isDarkMode ? 'bg-gray-800' : 'bg-gray-800'}`}></div>
      <div className={`h-4 w-3/4 mb-6 rounded ${isDarkMode ? 'bg-gray-800' : 'bg-gray-800'}`}></div>
      <div className="flex gap-2">
        <div className={`h-6 w-16 rounded-full ${isDarkMode ? 'bg-gray-800' : 'bg-gray-800'}`}></div>
        <div className={`h-6 w-16 rounded-full ${isDarkMode ? 'bg-gray-800' : 'bg-gray-800'}`}></div>
      </div>
    </div>;
};

// We need to preserve these functions from the original file since we used "keep existing code" for some sections
const handleFilterChange = (key: FilterKey, value: string | null) => {
  // This function exists in the original file and is referenced in our code
};

// Pagination Ellipsis
const PaginationEllipsis = ({
  className = ""
}) => {
  return <span className={`flex h-10 w-10 items-center justify-center ${className}`}>
      <span>…</span>
    </span>;
};
export default ResourcesPage;
